import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import type { AdCreative, Channel, PartnerFeature, PartnerTheme, VodTitle } from '../types';
import { ChannelCard } from './ChannelCard';

interface Props {
  theme: PartnerTheme;
  heroAd: AdCreative | null;
  channels: Channel[];
  movies: VodTitle[];
  series: VodTitle[];
  onPlayChannel: (channel: Channel) => void;
  onSelectTitle: (title: VodTitle) => void;
  onNavigate: (screen: PartnerFeature['target_screen']) => void;
  onAdImpression: (ad: AdCreative) => void;
  onAdSelect: (ad: AdCreative) => void;
}

function PartnerAdHero({ ad, onImpression, onSelect }: {
  ad: AdCreative;
  onImpression: (ad: AdCreative) => void;
  onSelect: (ad: AdCreative) => void;
}) {
  const container = useRef<HTMLElement | null>(null);
  const impressed = useRef(false);

  useEffect(() => {
    const element = container.current;
    if (!element || impressed.current) return;
    let viewableTimer: number | undefined;
    const observer = new IntersectionObserver(entries => {
      const viewable = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .5);
      window.clearTimeout(viewableTimer);
      if (!viewable || impressed.current) return;
      viewableTimer = window.setTimeout(() => {
        impressed.current = true;
        onImpression(ad);
        observer.disconnect();
      }, 1_000);
    }, { threshold: [.5] });
    observer.observe(element);
    return () => {
      window.clearTimeout(viewableTimer);
      observer.disconnect();
    };
  }, [ad, onImpression]);

  const image = <img src={ad.image} alt={ad.title} data-testid="partner-hero-ad-image" />;
  return <section
    ref={container}
    className="partner-ad-hero"
    data-testid="partner-hero-ad"
    data-ad-partner={ad.partner || 'global'}
    data-ad-format={`${ad.format?.width || 1920}x${ad.format?.height || 640}`}
  >
    <span>PUBLICIDADE</span>
    {ad.action_url ? <button
      type="button"
      data-focusable="true"
      data-autofocus="true"
      data-content-autofocus="true"
      aria-label={`${ad.title}. ${ad.action_label || 'Abrir anúncio'}`}
      onClick={() => onSelect(ad)}
    >{image}</button> : image}
  </section>;
}

function background(image: string): CSSProperties {
  return image ? { backgroundImage: `linear-gradient(90deg, rgba(8, 12, 18, .97) 0%, rgba(8, 12, 18, .72) 48%, rgba(8, 12, 18, .16) 100%), url("${image}")` } : {};
}

function eventDate(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo', weekday: 'short', day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(value)).replace('.', '').toUpperCase();
}

export function PartnerHubScreen({
  theme, heroAd, channels, movies, series, onPlayChannel, onSelectTitle, onNavigate,
  onAdImpression, onAdSelect,
}: Props) {
  const [highlight, ...features] = theme.features;
  const visibleFeatures = heroAd ? theme.features : features;
  const channelById = new Map(channels.map(channel => [channel.id, channel]));
  const titleById = new Map([...movies, ...series].map(title => [title.id, title]));

  return (
    <main className="content-shell partner-hub" data-testid="partner-hub">
      <header className="topbar">
        <div>
          <span className="eyebrow">EXPERIÊNCIA EM PARCERIA</span>
          <h1>{theme.name}</h1>
          <p>Uma seleção preparada para quem faz parte desta comunidade.</p>
        </div>
        <span className="topbar__status"><i /> PERFIL VINCULADO</span>
      </header>

      {heroAd ? <PartnerAdHero ad={heroAd} onImpression={onAdImpression} onSelect={onAdSelect} /> : <section className="partner-hero" style={background(highlight.image_url)} data-testid="partner-hero">
        <div className="partner-hero__content">
          <span className="eyebrow">{highlight.eyebrow}</span>
          <h2>{highlight.title}</h2>
          <p>{highlight.body}</p>
          <button data-focusable="true" data-autofocus="true" data-content-autofocus="true" data-testid={`partner-feature-${highlight.id}`} onClick={() => onNavigate(highlight.target_screen)}>
            {highlight.action_label}
          </button>
        </div>
      </section>}

      {visibleFeatures.length > 0 && <section className="partner-feature-section">
        <div className="section-heading">
          <div><span className="eyebrow">CONTEÚDO PARA VOCÊ</span><h2>Explore mais</h2></div>
        </div>
        <div className="partner-feature-grid">
          {visibleFeatures.map((feature, index) => <button
            key={feature.id}
            className="partner-feature-card"
            style={background(feature.image_url)}
            data-focusable="true"
            data-autofocus={heroAd && !heroAd.action_url && index === 0 ? 'true' : undefined}
            data-content-autofocus={heroAd && !heroAd.action_url && index === 0 ? 'true' : undefined}
            data-testid={`partner-feature-${feature.id}`}
            onClick={() => onNavigate(feature.target_screen)}
          >
            <span className="eyebrow">{feature.eyebrow}</span>
            <strong>{feature.title}</strong>
            <small>{feature.body}</small>
            <b>{feature.action_label} →</b>
          </button>)}
        </div>
      </section>}

      {theme.events.length > 0 && <section className="partner-agenda" data-testid="partner-agenda">
        <div className="home-section__header">
          <div><span className="eyebrow">PRÓXIMOS ENCONTROS</span><h2>Agenda da comunidade</h2></div>
          <small>{theme.events.length} programados</small>
        </div>
        <div className="partner-event-grid">
          {theme.events.map(event => <button
            key={event.id}
            className="partner-event-card"
            data-focusable="true"
            data-testid={`partner-event-${event.id}`}
            onClick={() => onNavigate(event.target_screen)}
          >
            <time dateTime={event.starts_at}>{eventDate(event.starts_at)}</time>
            <span className="eyebrow">{event.eyebrow}</span>
            <strong>{event.title}</strong>
            <small>{event.body}</small>
            <b>{event.action_label} →</b>
          </button>)}
        </div>
      </section>}

      {theme.rails.map(rail => {
        const railChannels = rail.items.flatMap(item => {
          const channel = item.channel_id ? channelById.get(item.channel_id) : undefined;
          return channel ? [channel] : [];
        });
        const railTitles = rail.items.flatMap(item => {
          const title = item.title_id ? titleById.get(item.title_id) : undefined;
          return title ? [title] : [];
        });
        if (!railChannels.length && !railTitles.length) return null;
        return <section className="partner-curation-section" key={rail.id} data-testid={`partner-rail-${rail.content_type}`}>
          <div className="home-section__header">
            <div><span className="eyebrow">{rail.eyebrow}</span><h2>{rail.title}</h2></div>
            <small>{rail.items.length} selecionados</small>
          </div>
          {rail.content_type === 'channels' ? <div className="partner-channel-rail">
            {railChannels.map(channel => <ChannelCard
              key={channel.id}
              channel={channel}
              onPlay={onPlayChannel}
              onPreview={() => undefined}
            />)}
          </div> : <div className="partner-vod-rail">
            {railTitles.map(title => <button
              key={title.id}
              className="home-vod-card"
              data-focusable="true"
              data-testid={`partner-title-${title.id}`}
              aria-label={title.name}
              onClick={() => onSelectTitle(title)}
            >
              <img src={title.poster} alt="" loading="lazy" />
              <span className="home-vod-card__focus-title">{title.name}</span>
            </button>)}
          </div>}
        </section>;
      })}
      <p className="partner-hub__notice">Conteúdo demonstrativo de homologação. A seleção final será definida com cada parceiro.</p>
    </main>
  );
}
